import React from "react";
import { Link } from "react-router-dom";
import { LegalLayout } from "../components/LegalLayout";
import { APP_NAME, SUPPORT_EMAIL, LEGAL_ROUTES } from "../lib/legal";

/**
 * /support — Public help centre.
 *
 * URL registered in App Store Connect as the app's support page.
 * Reachable without authentication, styled to match the legal pages,
 * SEO-optimised via ``LegalLayout``.
 */

function Section({ title, children }) {
  return (
    <section style={{ marginTop: 26 }}>
      <h2 style={{
        margin: "0 0 10px",
        fontSize: 20,
        fontWeight: 700,
        color: "var(--mf-text)",
        letterSpacing: -0.2,
        lineHeight: 1.3,
      }}>{title}</h2>
      {children}
    </section>
  );
}

function P({ children }) {
  return <p style={{ margin: "0 0 12px", lineHeight: 1.65 }}>{children}</p>;
}

function FaqItem({ q, children }) {
  return (
    <details
      style={{
        margin: "0 0 10px",
        padding: "12px 14px",
        background: "var(--mf-bg-card)",
        border: "1px solid var(--mf-divider)",
        borderRadius: 12,
      }}
    >
      <summary style={{
        cursor: "pointer",
        fontWeight: 600,
        color: "var(--mf-text)",
        listStyle: "none",
        fontSize: 15,
      }}>
        {q}
      </summary>
      <div style={{ marginTop: 10, color: "var(--mf-text-secondary)", fontSize: 15, lineHeight: 1.6 }}>
        {children}
      </div>
    </details>
  );
}

export default function Support() {
  const mailBody = [
    "Bonjour,",
    "",
    "Je rencontre un problème avec My Fleet.",
    "",
    "Adresse e-mail du compte :",
    "Modèle d’iPhone :",
    "Version iOS :",
    "Description du problème :",
    "",
    "Merci.",
  ].join("\n");
  const mailtoHref =
    `mailto:${SUPPORT_EMAIL}` +
    `?subject=${encodeURIComponent("Demande d’assistance My Fleet")}` +
    `&body=${encodeURIComponent(mailBody)}`;

  return (
    <LegalLayout
      title="Assistance"
      description="Besoin d’aide avec My Fleet ? Retrouvez les réponses aux questions fréquentes et contactez l’assistance."
      pathname={LEGAL_ROUTES.support}
    >
      <P>
        {APP_NAME} vous aide à gérer vos véhicules, entretiens, documents, kilométrages
        et échéances. Si vous rencontrez un problème, vous pouvez contacter notre assistance.
      </P>

      <Section title="Contact">
        <P>
          Adresse e-mail :{" "}
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            data-testid="support-email-link"
            style={{ color: "var(--mf-brand)", fontWeight: 600 }}
          >
            {SUPPORT_EMAIL}
          </a>
        </P>
        <P>Nous répondons généralement sous 24 à 48 heures ouvrées.</P>
        <a
          href={mailtoHref}
          data-testid="support-contact-button"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            marginTop: 6,
            padding: "12px 22px",
            borderRadius: 999,
            background: "var(--mf-brand)",
            color: "#fff",
            fontWeight: 700,
            fontSize: 15,
            textDecoration: "none",
          }}
        >
          Contacter l’assistance
        </a>
      </Section>

      <Section title="Questions fréquentes">
        <FaqItem q="Je n’arrive pas à me connecter">
          Vérifiez votre adresse e-mail et votre mot de passe. Vous pouvez également utiliser
          Google ou Apple si ces options sont disponibles.
        </FaqItem>
        <FaqItem q="Je souhaite modifier ou supprimer un véhicule">
          Ouvrez la fiche du véhicule concerné puis utilisez les options de modification ou de
          suppression.
        </FaqItem>
        <FaqItem q="Comment ajouter un document ou une facture ?">
          Ouvrez le véhicule concerné puis rendez-vous dans la section Documents ou Interventions.
        </FaqItem>
        <FaqItem q="Comment mettre à jour le kilométrage ?">
          Depuis la fiche du véhicule, utilisez l’option de mise à jour du kilométrage.
        </FaqItem>
        <FaqItem q="Comment supprimer mon compte ?">
          Dans l’application, ouvrez Profil puis sélectionnez Supprimer mon compte. Une page
          publique est également disponible :{" "}
          <Link to={LEGAL_ROUTES.deleteAccount} data-testid="support-faq-delete-link"
                style={{ color: "var(--mf-brand)" }}>
            {LEGAL_ROUTES.deleteAccount}
          </Link>.
        </FaqItem>
        <FaqItem q="Mes données sont-elles privées ?">
          Les données de votre compte sont utilisées uniquement pour fournir les fonctionnalités
          de {APP_NAME}. La politique de confidentialité est accessible ici :{" "}
          <Link to={LEGAL_ROUTES.privacy} data-testid="support-faq-privacy-link"
                style={{ color: "var(--mf-brand)" }}>
            {LEGAL_ROUTES.privacy}
          </Link>.
        </FaqItem>
      </Section>

      <Section title="Informations utiles">
        <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.8 }}>
          <li>
            <Link to={LEGAL_ROUTES.privacy} data-testid="support-link-privacy"
                  style={{ color: "var(--mf-brand)" }}>
              Politique de confidentialité
            </Link>
          </li>
          <li>
            <Link to={LEGAL_ROUTES.terms} data-testid="support-link-terms"
                  style={{ color: "var(--mf-brand)" }}>
              Conditions d’utilisation
            </Link>
          </li>
          <li>
            <Link to={LEGAL_ROUTES.legal} data-testid="support-link-legal"
                  style={{ color: "var(--mf-brand)" }}>
              Mentions légales
            </Link>
          </li>
          <li>
            <Link to={LEGAL_ROUTES.deleteAccount} data-testid="support-link-delete"
                  style={{ color: "var(--mf-brand)" }}>
              Suppression du compte
            </Link>
          </li>
        </ul>
      </Section>
    </LegalLayout>
  );
}
